import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuIcon from "@mui/icons-material/Menu";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { API_get_learning_section_detail } from "../../../constants";
import { getRightworkAPI } from "../requests/utilities/requests";
import SectionPreview from "./SectionPreview";
import { buildPreviewSchema } from "./trainingBlockSpecs";
import TrackSidebarModuleDropdown from "./TrackSidebarModuleDropdown";

const modalStyle = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  bgcolor: "background.paper",
  borderRadius: 0,
  boxShadow: "none",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const mobileModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  height: "90vh",
  maxHeight: "900px",
  aspectRatio: "9 / 16",
  borderRadius: "16px",
  overflow: "hidden",
  bgcolor: "background.paper",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.4)",
};

function LearningContentPreviewModal({
  open,
  onClose,
  previewType,
  item,
  homeCompany,
  quizzesData,
  language,
  initialDevice = "desktop",
}) {
  const [device, setDevice] = React.useState(initialDevice);
  const [selectedModuleIndex, setSelectedModuleIndex] = React.useState(0);
  const [selectedSectionIndex, setSelectedSectionIndex] = React.useState(0);
  const [sectionDetail, setSectionDetail] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [showDesktopOutline, setShowDesktopOutline] = React.useState(true);

  const moduleCountLabel = (count) => {
    if (language === "chinese") return `${count} 个模块`;
    if (language === "spanish") {
      return `${count} ${count === 1 ? "módulo" : "módulos"}`;
    }
    return `${count} ${count === 1 ? "module" : "modules"}`;
  };

  const modules = React.useMemo(() => {
    if (!item) return [];
    if (previewType === "track") {
      return item.modules || [];
    }
    if (previewType === "module") {
      return [
        {
          ...item,
          sections: item.sections || [],
        },
      ];
    }
    return [
      {
        uuid: "single-section-preview",
        name: "Preview",
        color: "bg-violet-700",
        sections: [item],
      },
    ];
  }, [item, previewType]);

  const title = item?.name || "Training preview";
  const previewModules = React.useMemo(
    () =>
      modules.map((module) => {
        const sections = (module.sections || []).map((section) => ({
          ...section,
          annotated_enrollment: {
            is_completed: false,
            ...(section.annotated_enrollment || {}),
          },
        }));
        const completedSections = sections.filter(
          (section) => section.annotated_enrollment?.is_completed
        ).length;
        return {
          ...module,
          sections,
          annotated_enrollment: {
            sections_completed_count:
              module.annotated_enrollment?.sections_completed_count ??
              completedSections,
            total_sections_count:
              module.annotated_enrollment?.total_sections_count ??
              sections.length,
          },
        };
      }),
    [modules]
  );
  const selectedModule = modules[selectedModuleIndex] || null;
  const sections = selectedModule?.sections || [];
  const selectedSection = sections[selectedSectionIndex] || null;
  const sectionDetailMatchesSelection =
    !sectionDetail?.uuid ||
    !selectedSection?.uuid ||
    sectionDetail.uuid === selectedSection.uuid;
  const selectedSectionTitle = sectionDetailMatchesSelection
    ? sectionDetail?.name || selectedSection?.name || title
    : selectedSection?.name || title;
  const totalSections = modules.reduce(
    (sum, module) => sum + (module.sections?.length || 0),
    0
  );
  const hasContent = totalSections > 0;

  const schema = React.useMemo(
    () =>
      buildPreviewSchema({
        homeCompany,
        quizzesData,
        previewLanguage: language,
      }),
    [homeCompany, quizzesData, language]
  );

  React.useEffect(() => {
    if (!open || !selectedSection) {
      setIsLoading(false);
      if (!selectedSection) {
        setSectionDetail(null);
      }
      return;
    }
    let cancelled = false;

    if (Array.isArray(selectedSection.as_json)) {
      setSectionDetail(selectedSection);
      setIsLoading(false);
      return;
    }

    if (!selectedSection.uuid) {
      setSectionDetail(selectedSection);
      setIsLoading(false);
      return;
    }

    const fetchSectionDetail = async () => {
      setIsLoading(true);
      try {
        const res = await getRightworkAPI(API_get_learning_section_detail, {
          company_id: homeCompany,
          learning_section_uuid: selectedSection.uuid,
          language: language || "english",
        });
        if (cancelled) return;
        const learningSection = res.data?.redux?.payload?.learning_section;
        const rawBlocksAsJson = learningSection?.as_json || [];
        let mapSignedUrlToFileUuid =
          localStorage.getItem("mapSignedUrlToFileUuid") || "{}";
        mapSignedUrlToFileUuid = JSON.parse(mapSignedUrlToFileUuid);
        rawBlocksAsJson.forEach((block) => {
          const validTypes = ["image", "video", "audio", "file", "pdf"];
          if (!validTypes.includes(block.type)) return;
          if (!block.props?.url || !block.props?.file_uuid) return;
          mapSignedUrlToFileUuid[block.props.url] = block.props.file_uuid;
        });
        localStorage.setItem(
          "mapSignedUrlToFileUuid",
          JSON.stringify(mapSignedUrlToFileUuid)
        );
        setSectionDetail(learningSection || selectedSection);
      } catch (error) {
        if (!cancelled) {
          console.error("Preview section fetch failed", error);
          setSectionDetail(selectedSection);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchSectionDetail();
    return () => {
      cancelled = true;
    };
  }, [open, selectedSection, homeCompany, language]);

  const selectSection = (moduleIndex, sectionIndex) => {
    if (
      moduleIndex === selectedModuleIndex &&
      sectionIndex === selectedSectionIndex
    ) {
      return;
    }
    setSelectedModuleIndex(moduleIndex);
    setSelectedSectionIndex(sectionIndex);
    setSectionDetail(null);
  };

  const flatSections = React.useMemo(() => {
    const rows = [];
    modules.forEach((module, moduleIndex) => {
      (module.sections || []).forEach((section, sectionIndex) => {
        rows.push({ module, moduleIndex, section, sectionIndex });
      });
    });
    return rows;
  }, [modules]);

  React.useEffect(() => {
    if (!open) return;
    const firstSectionRow = flatSections[0];
    setDevice(initialDevice);
    setSelectedModuleIndex(firstSectionRow?.moduleIndex || 0);
    setSelectedSectionIndex(firstSectionRow?.sectionIndex || 0);
    setSectionDetail(
      Array.isArray(firstSectionRow?.section?.as_json)
        ? firstSectionRow.section
        : null
    );
    setShowDesktopOutline(true);
  }, [
    open,
    item?.uuid,
    item?._previewKey,
    previewType,
    initialDevice,
    flatSections,
  ]);

  const flatIndex = flatSections.findIndex(
    (row) =>
      row.moduleIndex === selectedModuleIndex &&
      row.sectionIndex === selectedSectionIndex
  );
  const canGoPrev = flatIndex > 0;
  const canGoNext = flatIndex >= 0 && flatIndex < flatSections.length - 1;

  const goToFlatIndex = (nextIndex) => {
    const row = flatSections[nextIndex];
    if (!row) return;
    selectSection(row.moduleIndex, row.sectionIndex);
  };

  const renderSectionBody = (mobilePreview = false) => {
    if (isLoading) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-white">
          <LoadSpinner />
        </div>
      );
    }
    if (hasContent) {
      return (
        <div
          className={
            mobilePreview
              ? "bn-reader-mobile-tight bn-reader-mobile-preview"
              : "bn-reader-mobile-tight"
          }
        >
          <SectionPreview
            key={
              sectionDetail?._previewKey ||
              selectedSection?._previewKey ||
              sectionDetail?.uuid ||
              selectedSection?.uuid ||
              "preview"
            }
            schema={schema}
            asJson={sectionDetail?.as_json}
          />
        </div>
      );
    }
    return (
      <div className="flex h-full items-center justify-center text-center">
        <div>
          <p className="text-sm font-medium text-slate-600">
            Nothing to preview yet.
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Add at least one section to see the employee view.
          </p>
        </div>
      </div>
    );
  };

  const desktopPreviewPane = (
    <div className="flex h-full w-full flex-col bg-white">
      <div className="shrink-0">
        <div className="relative bg-white px-12 py-2.5 text-center shadow">
          {!showDesktopOutline && (
            <button
              type="button"
              aria-label="Show course outline"
              onClick={() => setShowDesktopOutline(true)}
              className="absolute left-2 top-2 flex items-center text-slate-500 transition-colors hover:text-slate-600"
            >
              <MenuIcon style={{ fontSize: 22 }} />
            </button>
          )}
          <p className="text-xs font-semibold uppercase text-slate-400">
            Module {selectedModuleIndex + 1}
            {selectedSection ? ` - Section ${selectedSectionIndex + 1}` : ""}
          </p>
          <h3 className="mt-0.5 line-clamp-2 text-lg font-semibold text-slate-900">
            {selectedSectionTitle}
          </h3>
        </div>
        <div className="h-1.5 bg-slate-100 shadow-inner" />
      </div>
      <div className="relative flex-1 overflow-auto bg-white pt-5 shadow">
        {renderSectionBody(false)}
      </div>
      {hasContent && (
        <div className="w-full shrink-0 flex flex-col">
          <div className="h-1.5 bg-slate-100 shadow-inner" />
          <div className="flex items-center justify-between gap-3 bg-white px-6 py-3 shadow">
            <div
              title="Preview only"
              className="flex h-fit items-center justify-center gap-3 rounded-lg bg-emerald-600 px-4 py-2 text-sm text-white"
            >
              Mark As Completed
              <TaskAltIcon />
            </div>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                disabled={!canGoPrev}
                onClick={() => goToFlatIndex(flatIndex - 1)}
                className="flex items-center justify-center gap-x-1.5 rounded-lg bg-violet-700 px-4 py-2 text-sm text-white transition-colors hover:bg-violet-800 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
              >
                <ChevronLeftIcon />
                <span>Last Section</span>
              </button>
              <button
                type="button"
                disabled={!canGoNext}
                onClick={() => goToFlatIndex(flatIndex + 1)}
                className="flex items-center justify-center gap-x-1.5 rounded-lg bg-violet-700 px-4 py-2 text-sm text-white transition-colors hover:bg-violet-800 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
              >
                <span>Next Section</span>
                <ChevronRightIcon />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const mobileLearnerPane = (
    <div className="flex h-full flex-col bg-white">
      <div className="shrink-0">
        <div className="relative flex justify-center bg-white px-10 py-3 shadow">
          <div className="absolute left-2 top-2 flex items-center text-slate-500">
            <MenuIcon style={{ fontSize: 22 }} />
          </div>
          <div className="min-w-0 text-center">
            <p className="text-xs font-semibold uppercase text-slate-400">
              MODULE {selectedModuleIndex + 1}
              {selectedSection ? ` - Section ${selectedSectionIndex + 1}` : ""}
            </p>
            <h3 className="mt-0.5 line-clamp-2 text-base font-semibold text-slate-900">
              {selectedSectionTitle}
            </h3>
          </div>
        </div>
        <div className="h-1.5 bg-slate-100 shadow-inner" />
      </div>

      <div className="relative flex-1 overflow-auto bg-white pt-5 shadow">
        {renderSectionBody(true)}
      </div>

      {hasContent && (
        <div className="w-full shrink-0 flex flex-col">
          <div className="h-1.5 bg-slate-100 shadow-inner" />
          <div className="flex items-center justify-between gap-3 bg-white px-4 py-3 shadow">
            <div
              title="Preview only"
              className="flex h-fit items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm text-white"
            >
              Complete
              <TaskAltIcon style={{ fontSize: 18 }} />
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!canGoPrev}
                onClick={() => goToFlatIndex(flatIndex - 1)}
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-700 text-white disabled:bg-slate-200 disabled:text-slate-400"
              >
                <ChevronLeftIcon fontSize="small" />
              </button>
              <button
                type="button"
                disabled={!canGoNext}
                onClick={() => goToFlatIndex(flatIndex + 1)}
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-700 text-white disabled:bg-slate-200 disabled:text-slate-400"
              >
                <ChevronRightIcon fontSize="small" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const isMobilePreview = device === "mobile";
  const modalSlotProps = isMobilePreview
    ? {
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.95)",
          },
        },
      }
    : undefined;

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableEnforceFocus
      slotProps={modalSlotProps}
    >
      <Box sx={isMobilePreview ? mobileModalStyle : modalStyle}>
        <button
          type="button"
          aria-label="Close preview"
          onClick={onClose}
          className={`absolute right-3 top-3 z-50 flex h-9 w-9 items-center justify-center text-slate-500 transition-colors hover:text-slate-700 ${
            isMobilePreview
              ? "rounded-full bg-white/90 shadow hover:bg-white"
              : "rounded-lg hover:bg-white"
          }`}
        >
          <CloseIcon />
        </button>

        {isMobilePreview ? (
          mobileLearnerPane
        ) : (
          <div className="flex min-h-0 flex-1 bg-slate-50">
            {showDesktopOutline && (
              <div className="w-[300px] shrink-0 overflow-auto border-r border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-4 pb-6 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowDesktopOutline(false)}
                    className="flex items-center space-x-1.5 text-slate-500 transition-colors hover:text-slate-600"
                  >
                    <MenuIcon style={{ fontSize: 22, marginBottom: 2 }} />
                    <span className="text-xs">Hide</span>
                  </button>
                  <h2 className="mt-8 text-2xl text-slate-900">{title}</h2>
                  <div className="mt-3 flex items-center space-x-2">
                    <div className="h-3 w-3 rounded bg-violet-700" />
                    <p className="text-xs text-slate-900">
                      {moduleCountLabel(previewModules.length)}
                    </p>
                  </div>
                  <div className="mt-6 flex items-center justify-between">
                    <p className="text-xxs text-slate-500">0% completed</p>
                  </div>
                  <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-1.5 rounded-full bg-emerald-600"
                      style={{ width: "0%" }}
                    />
                  </div>
                </div>
                {previewModules.map((module, moduleIndex) => (
                  <TrackSidebarModuleDropdown
                    key={module.uuid || moduleIndex}
                    module={module}
                    index={moduleIndex}
                    selectedModuleIndex={selectedModuleIndex}
                    selectedSectionIndex={selectedSectionIndex}
                    onSelectSection={selectSection}
                    userType="Manager"
                    isLoading={isLoading}
                    language={language}
                  />
                ))}
                {!hasContent && (
                  <div className="px-4 py-12 text-center">
                    <ViewModuleIcon
                      className="text-slate-300"
                      style={{ fontSize: 44 }}
                    />
                    <p className="mt-3 text-xs text-slate-500">
                      No sections available.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex min-w-0 flex-1 overflow-hidden bg-white">
              {desktopPreviewPane}
            </div>
          </div>
        )}
      </Box>
    </Modal>
  );
}

export default LearningContentPreviewModal;
